import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { formatCurrency, formatShortDate, formatDate } from "@/lib/format";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { useTranslation } from "react-i18next";

const PIE_COLORS = ["#ff7b02", "#2563EB", "#14B8A6", "#F59E0B", "#DC2626"];

export default function SuperReports() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [mrr, setMrr] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [plans, setPlans] = useState([]);
    const [subs, setSubs] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/mrr-series?months=12"),
            api.get("/admin/reservations-series?days=30"),
            api.get("/billing/plans"),
            api.get("/billing/subscriptions"),
        ]).then(([s, m, r, p, sub]) => { setStats(s.data); setMrr(m.data); setReservations(r.data); setPlans(p.data); setSubs(sub.data); });
    }, []);

    // Plan distribution
    const planDist = plans.map((p) => ({
        name: p.name,
        value: subs.filter((s) => s.plan_id === p.id && s.status === "active").length,
    })).filter((d) => d.value > 0);

    return (
        <>
            <Topbar title={t("super_admin.reports_title")} subtitle={t("super_admin.overview_subtitle")} />
            <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <KPI label="MRR" value={formatCurrency(stats?.mrr || 0)} />
                    <KPI label="ARR" value={formatCurrency(stats?.arr || 0)} />
                    <KPI label="Active subs" value={stats?.active_subscriptions ?? 0} />
                    <KPI label="Reservations · 30d" value={stats?.reservations_processed_30d ?? 0} />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <Card title="MRR · last 12 months" className="lg:col-span-2">
                        <div className="h-[260px]">
                            <ResponsiveContainer>
                                <AreaChart data={mrr} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="mrr" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.18} />
                                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
                                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} width={50} />
                                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} formatter={(v) => formatCurrency(v)} />
                                    <Area type="monotone" dataKey="mrr" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#mrr)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <Card title="Plan distribution">
                        <div className="h-[260px]">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={planDist} dataKey="value" nameKey="name" outerRadius={80} innerRadius={50}>
                                        {planDist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <Card title="Reservations · last 30 days" className="lg:col-span-3">
                        <div className="h-[220px]">
                            <ResponsiveContainer>
                                <BarChart data={reservations}>
                                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
                                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} width={32} />
                                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} labelFormatter={(d) => formatDate(d)} />
                                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
}

function KPI({ label, value }) {
    return (
        <div className="akm-card p-3">
            <div className="akm-section-title">{label}</div>
            <div className="mt-1 text-lg font-semibold tracking-tight">{value}</div>
        </div>
    );
}

function Card({ title, children, className = "" }) {
    return (
        <div className={`akm-card p-4 ${className}`}>
            <div className="mb-3 akm-section-title">{title}</div>
            {children}
        </div>
    );
}
